import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        // Legacy aliases for backward compatibility
        primary: "bg-primary text-primary-foreground hover:bg-primary/90",
        danger:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        md: "h-9 px-4 py-2 has-[>svg]:px-3", // Legacy alias for backward compatibility
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading,
  disabled,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    loading?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  // `loading` is not supported under `asChild`, and is ignored rather than
  // approximated. Two reasons; the second is the one that decided the shape of
  // this component.
  //
  // 1. `loading` has no coherent meaning on a slotted child. The spinner would
  //    be injected into an element whose children we do not own, and `loading`
  //    also implies `disabled`, which is not a state an `<a>` has. A "loading
  //    link" is not a thing.
  //
  // 2. `Slot` calls `React.Children.only`, so it must receive exactly one
  //    element. `{loading && <svg/>}{children}` is *always* a two-element array
  //    — JSX builds the array from the source, not from the runtime values, so
  //    `asChild` threw even with `loading` unset (#82). The obvious fix, wrapping
  //    the pair in a fragment, stops the throw and is worse: `Slot` would then
  //    clone the fragment, and fragments accept nothing but `key` and
  //    `children`, so `className`, `data-slot` and every handler are dropped on
  //    the floor and `asChild` silently stops doing its job.
  const isLoading = !asChild && loading

  // Read `process` off `globalThis` rather than referencing it directly: this is
  // a browser library, and pulling in `@types/node` to type one dev warning
  // would put every Node global in scope for a package that must not use them.
  // Vite defines `process.env.NODE_ENV` in the consuming app, so the runtime
  // check works; this only avoids depending on ambient Node types to express it.
  const isProduction =
    (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process?.env
      ?.NODE_ENV === "production"

  if (!isProduction && asChild && loading) {
    console.warn(
      "[Button] `loading` is ignored when `asChild` is set — a slotted child " +
        "cannot carry the spinner or the disabled state. Render the loading UI " +
        "inside the child, or drop `asChild`."
    )
  }

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }), isLoading && "opacity-50 cursor-wait")}
      // Under `asChild`, pass the caller's `disabled` straight through rather
      // than `disabled || loading`, so a slotted `<a>` never receives an
      // attribute it has no meaning for.
      disabled={asChild ? disabled : disabled || loading}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {isLoading && (
            <svg
              className="mr-2 h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}
          {children}
        </>
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
